import { useEffect, useRef } from 'react';
import { API_URL, getApiHeaders } from '../config/apiConfig';
import { isSteamClient } from '../utils/isSteamClient';

/**
 * Mirrors server-confirmed title unlocks to Steamworks on the Steam Electron build.
 * Regular clients must not call this.
 */
export function useSteamAchievements(isLoggedIn) {
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!isSteamClient() || !isLoggedIn) {
      return undefined;
    }
    if (!window.electron || !window.electron.steam) {
      throw new Error('useSteamAchievements requires Steam IPC. Launch CODA from Steam.');
    }

    let cancelled = false;

    async function syncSteamAchievements() {
      if (syncingRef.current) {
        return;
      }
      syncingRef.current = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('token is required to sync Steam achievements');
        }
        const res = await fetch(`${API_URL}/integrations/steam/unlocked-achievements`, {
          headers: getApiHeaders(true),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Steam achievement sync failed (${res.status})`);
        }
        const data = await res.json();
        if (!data || !Array.isArray(data.unlocked)) {
          throw new Error('Steam achievement sync response must include an unlocked array');
        }
        if (cancelled || data.unlocked.length === 0) {
          return;
        }
        const steamApiNames = data.unlocked.map((row) => {
          if (!row || !row.steamApiName) {
            throw new Error('Steam achievement sync row is missing steamApiName');
          }
          return row.steamApiName;
        });
        await window.electron.steam.unlockAchievements(steamApiNames);
        const achievementKeys = data.unlocked.map((row) => row.achievementKey);
        const ackRes = await fetch(`${API_URL}/integrations/steam/ack-unlocks`, {
          method: 'POST',
          headers: getApiHeaders(true),
          body: JSON.stringify({ achievementKeys }),
        });
        if (!ackRes.ok) {
          const body = await ackRes.json().catch(() => ({}));
          throw new Error(body.error || `Steam achievement ACK failed (${ackRes.status})`);
        }
      } catch (err) {
        console.error('Steam achievement sync failed:', err);
      } finally {
        syncingRef.current = false;
      }
    }

    syncSteamAchievements();
    const intervalId = setInterval(syncSteamAchievements, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isLoggedIn]);
}

export default useSteamAchievements;
